import { useId } from 'react';
import { Star } from 'lucide-react';
import { cx } from './charte';
import { scoreLabel, starFill, starScore } from '../lib/instructorRating';

const STARS = [1, 2, 3, 4, 5];

/**
 * Note sur 5 étoiles par demi-étoile : moitié gauche d'une étoile = demi-point. Dix boutons radio
 * (masqués) posés sur les moitiés d'étoile : flèches du clavier, lecteur d'écran et toucher
 * fonctionnent. Toucher de nouveau la note choisie l'efface quand `clearable`.
 */
export function StarRating({
  value,
  onChange,
  label,
  size = 'lg',
  clearable = false,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  label: string;
  size?: 'lg' | 'sm';
  clearable?: boolean;
}) {
  const name = useId();
  const px = size === 'lg' ? 40 : 24;
  return (
    <div role="radiogroup" aria-label={label} className={cx('flex items-center', size === 'lg' ? 'gap-1.5' : 'gap-1')}>
      {STARS.map((star) => {
        const fill = starFill(star, value);
        return (
          <span key={star} className="relative inline-block shrink-0" style={{ width: px, height: px }}>
            <Star size={px} strokeWidth={1.5} className="absolute inset-0 text-slate-300" aria-hidden />
            {fill > 0 && (
              <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: fill === 1 ? px : px / 2 }} aria-hidden>
                <Star size={px} strokeWidth={1.5} className="text-amber-400" fill="currentColor" />
              </span>
            )}
            {[true, false].map((left) => {
              const score = starScore(star, left);
              return (
                <label
                  key={score}
                  className={cx(
                    'absolute inset-y-0 cursor-pointer rounded-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-200',
                    left ? 'left-0 w-1/2' : 'right-0 w-1/2',
                  )}
                >
                  <input
                    type="radio"
                    name={name}
                    value={score}
                    checked={value === score}
                    onChange={() => onChange(score)}
                    onClick={() => {
                      if (clearable && value === score) onChange(null);
                    }}
                    className="sr-only"
                  />
                  <span className="sr-only">{`${scoreLabel(score)} étoile${score > 1 ? 's' : ''}`}</span>
                </label>
              );
            })}
          </span>
        );
      })}
    </div>
  );
}
